<!-- Backdrop for mobile -->
<div 
  *ngIf="isMobileOpen$ | async" 
  class="mobile-only-backdrop"
  (click)="closeMobileMenu()"
></div>

<aside 
  class="sidebar h-full transition-all duration-300 z-50"
  [class.collapsed]="isCollapsed"
  [class.mobile-open]="isMobileOpen$ | async"
>
  <!-- Sidebar Header. Holds ONLY the mobile close button, which is display:none
       on desktop - so without the sidebar-header rule below this collapses to a
       32px band of nothing above the first menu item. -->
  <div class="sidebar-header p-4 flex justify-between items-center relative">
    <!-- Mobile Close Button (Hamburger variant) -->
    <button 
      (click)="closeMobileMenu()"
      class="mobile-only-close p-2 text-slate-500 hover:text-slate-800"
    >
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
      </svg>
    </button>
  </div>

  <!-- Desktop Collapse Button (Repositioned to right edge) -->
  <button 
    (click)="toggleSidebar()"
    class="desktop-only-toggle toggle-btn top-3"
  >
    <svg 
      class="w-3 h-3 transition-transform duration-200" 
      [class.rotate-180]="isCollapsed"
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M15 19l-7-7 7-7" />
    </svg>
  </button>

  <!-- Menu Items -->
  <nav class="sidebar-nav flex-1 overflow-y-auto py-2 scrollbar-thin">
    <div *ngFor="let item of visibleMenuItems">
      <!-- Main Menu Item -->
      <div 
        [routerLink]="item.hasSubmenu ? null : item.route"
        (click)="item.hasSubmenu ? toggleSubmenu(item.text) : null"
        routerLinkActive="active"
        [routerLinkActiveOptions]="{exact: false}"
        class="menu-item group"
        [class.active]="item.route !== '/' && activePage.includes(item.route)"
        [class.collapsed]="isCollapsed"
        [pTooltip]="isCollapsed ? (item.text | translate) : ''"
        tooltipPosition="right"
        tooltipStyleClass="sidebar-tooltip"
      >
        <!-- The badge is anchored to the ICON, not placed beside it in the row.
             As a flow sibling it was a third flex child, and .menu-item.collapsed
             centres its children as a group — so the dot widened the row and
             shunted the icon off-centre against every other item. Absolute takes
             it out of flow, so the row measures the same with or without it.
             The wrapper exists because .icon-container is an [innerHTML] target:
             anything nested inside it would be wiped on the next render. -->
        <div class="relative flex-shrink-0">
          <div class="icon-container" [innerHTML]="item.icon"></div>
          <span *ngIf="item.hasNews || (item.route === '/schedule' && hasPendingRequests())"
            class="absolute -top-0.5 -right-0.5 flex w-2 h-2">
            <span
              class="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping motion-reduce:animate-none"></span>
            <span
              class="relative inline-flex w-2 h-2 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]"></span>
          </span>
          <i *ngIf="item.locked && isCollapsed" class="pi pi-lock absolute -bottom-1 -right-1.5 text-[9px] text-slate-400"
            aria-hidden="true"></i>
        </div>

        <span *ngIf="!isCollapsed" class="ml-3 text-sm font-medium flex-1">
          {{ item.text | translate }}<i *ngIf="item.locked" class="pi pi-lock ml-1.5 align-[-1px] text-[14px] text-slate-400"
            aria-hidden="true"></i>
        </span>
        <span *ngIf="item.locked" class="sr-only">{{ 'feedback.locked_glyph' | translate }}</span>

        <svg 
          *ngIf="item.hasSubmenu && !isCollapsed" 
          class="w-4 h-4 text-slate-400 transition-transform duration-200"
          [class.rotate-180]="expandedSubmenu === item.text"
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      <!-- Submenu Items -->
      <div 
        *ngIf="item.hasSubmenu && expandedSubmenu === item.text && !isCollapsed"
        class="submenu"
      >
        <a 
          *ngFor="let subitem of item.submenuItems"
          [routerLink]="subitem.route"
          routerLinkActive="active-sub"
          class="submenu-item"
        >
          {{ subitem.text | translate }}
        </a>
      </div>
    </div>
  </nav>

  <!-- CRM platform. Sits outside <nav> because it leaves the app entirely —
       it is not a route, so it gets no routerLinkActive and never highlights.
       Styled with .menu-item so it still reads as part of the same list.

       The external-link arrow is hidden while collapsed rather than shrunk:
       .menu-item.collapsed centres its children as a group, so a second
       in-flow child would shove the icon off the line every other icon sits
       on. Same reason the label is hidden there. -->
  <div *ngIf="canShowCrmLink" class="pt-1 pb-2 border-t border-slate-100">
    <div
      class="menu-item group"
      [class.collapsed]="isCollapsed"
      [class.opacity-60]="crmSsoLoading"
      [pTooltip]="isCollapsed ? ('sidebar_crm_platform' | translate) : ''"
      tooltipPosition="right"
      tooltipStyleClass="sidebar-tooltip"
      (click)="openCrmPlatform()"
    >
      <div class="relative flex-shrink-0">
        <div class="icon-container">
          <img src="assets/images/CRM.png" alt="" aria-hidden="true"
            class="w-5 h-5 object-contain rounded-[4px]">
        </div>
      </div>

      <span *ngIf="!isCollapsed" class="ml-3 text-sm font-medium flex-1">
        {{ 'sidebar_crm_platform' | translate }}
      </span>

      <i *ngIf="!isCollapsed"
        class="text-[10px] text-slate-400 group-hover:text-primary transition-colors"
        [ngClass]="crmSsoLoading ? 'pi pi-spin pi-spinner' : 'pi pi-external-link'"></i>
    </div>
  </div>

</aside>
